import { FC, memo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useUser } from '@hooks/api/useUser';
import ActiveTab from '@pages/LeadsDatabase/components/ActiveTab';
import ToggleTabs from '@pages/LeadsDatabase/components/ToggleTabs';
import { LeadsDatabaseProvider } from '@pages/LeadsDatabase/context';
import styled from 'styled-components';

// Theme-v2 components
import { PageHeader } from '@components/theme-v2/components/PageHeader';
import { Card } from '@components/theme-v2/components/Card';

import { useScreenSizeContext } from '@/shared/context/ScreenSizeContext';

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--sk-space-6);
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    gap: var(--sk-space-4);
  }
`;

const TabsCard = styled.div`
  background: var(--sk-surface-default);
  border: 1px solid var(--sk-surface-border);
  border-radius: var(--sk-radius-lg);
  overflow: hidden;
  width: 100%;

  @media (max-width: 768px) {
    border-radius: var(--sk-radius-default);
  }
`;

const TabsHeader = styled.div`
  padding: var(--sk-space-4) var(--sk-space-6);
  border-bottom: 1px solid var(--sk-surface-border);
  background: var(--sk-bg-secondary);
  overflow-x: auto;

  @media (max-width: 768px) {
    padding: var(--sk-space-3) var(--sk-space-4);
  }
`;

const TabsContent = styled.div`
  padding: var(--sk-space-6);
  overflow-x: auto;

  @media (max-width: 768px) {
    padding: var(--sk-space-4);
  }
`;

const LoadingWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--sk-space-12);
  color: var(--sk-text-secondary);
  font-family: var(--sk-font-display);
`;

const LeadsDatabase: FC = () => {
  const { isLoading } = useUser();
  const { t } = useTranslation();
  const { isLargerScreen } = useScreenSizeContext();

  if (isLoading) {
    return <LoadingWrapper>Loading...</LoadingWrapper>;
  }

  return (
    <>
      <Helmet>
        <title>{t('common.helmetTitles.leadsDatabase')}</title>
      </Helmet>
      <LeadsDatabaseProvider>
        <PageWrapper>
          <PageHeader
            title="Lead Database"
            subtitle="Browse, search, and export your scraped leads collection"
            breadcrumbs={[
              { label: 'Home', href: '/' },
              { label: 'Lead Database' }
            ]}
          />

          <TabsCard>
            <TabsHeader>
              <ToggleTabs />
            </TabsHeader>
            <TabsContent>
              <ActiveTab />
            </TabsContent>
          </TabsCard>
        </PageWrapper>
      </LeadsDatabaseProvider>
    </>
  );
};

export default memo(LeadsDatabase);
